/**
 * Shape compound tool — WP-559 (Slice 2 of WP-557, TEN-2532).
 *
 * A genuinely NEW noun (mirrors workspace.ts's own S2 precedent: "the one genuinely NEW
 * name... every other core noun reuses an existing name") — no existing compound tool owns
 * the shape-advisory disposition domain, and naming the actions `shape action=agree`/
 * `action=dismiss` (rather than a hypothetical `shape-agree` action on some unrelated tool)
 * avoids the noun-repeats-verb stutter the constitution's naming section flags. Mirrors the
 * CLI 1:1: `pb shape list/show/agree/dismiss` <-> `shape` tool `action=list/show/agree/dismiss`.
 *
 * Actions:
 *   list    — List shape advisories for this workspace, latest per subject
 *   show    — Show one shape advisory by id
 *   agree   — Agree with a compound advisory's split verdict
 *   dismiss — Dismiss a compound advisory's split verdict
 *
 * agree/dismiss route through the SAME `chain.dispositionShapeAdvisory` mutation the CLI
 * calls — one business-logic path (convex/shape/disposition.ts), never a second hand-rolled
 * copy. The typed `dispositionBy` actor is derived server-side from the gateway-injected
 * `surface` (convex/http.ts's `injectSurface`, driven by the `x-pb-source` header this MCP
 * client already sends) — an MCP call's surface is always `'mcp'`, so it is always
 * classified `'agent'` (see disposition.ts's header for why credential type, which the CLI
 * and MCP share, can't be the signal here).
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { thinWrapper, parseOrFail, successResult, failureResult, type ToolResult } from "../envelope.js";
import { kernelQuery, kernelMutation, kernelCallEnvelope, getWorkspaceId, requireWriteAccess, runWithToolContext } from "../client.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";
import { classifyError } from "../errors.js";

const SHAPE_ACTIONS = ["list", "show", "agree", "dismiss"] as const;
type ShapeAction = (typeof SHAPE_ACTIONS)[number];

const LIST_DISPOSITIONS = ["pending", "agreed", "dismissed", "expired"] as const;
const LIST_OUTCOMES = ["not_candidate", "atomic", "compound", "unavailable"] as const;

interface ShapeAdvisoryItem {
  id: string;
  outcome: string;
  disposition: string;
  subjectEntryId: string;
  collectionSlug: string;
  source: string;
  unavailableReason?: string;
  dispositionReason?: string;
  proposedAtoms?: Array<{ title: string; concern: string; bodyExcerpt: string; suggestedCollection?: string }>;
  proposedLinks?: Array<{ fromIdx: number; toIdx: number; relation: string }>;
  [key: string]: unknown;
}

interface DispositionReceipt {
  rowId: string;
  disposition: "agreed" | "dismissed";
  dispositionBy: { kind: "human" | "agent"; personId?: string };
}

// §3a — the REGISTERED layer: flat ZodObject, action enum + superset of params.
export const shapeSchema = z.object({
  action: z.enum(SHAPE_ACTIONS).describe(
    "'list': list shape advisories for this workspace, latest per subject. " +
      "'show': show one shape advisory by id. " +
      "'agree': agree with a compound advisory's split verdict. " +
      "'dismiss': dismiss a compound advisory's split verdict.",
  ),
  disposition: z.enum(LIST_DISPOSITIONS).optional().describe(
    "For 'list': filter by disposition. Omitting both disposition and outcome defaults to the actionable set (disposition:pending, outcome:compound).",
  ),
  outcome: z.enum(LIST_OUTCOMES).optional().describe(
    "For 'list': filter by outcome. Omitting both disposition and outcome defaults to the actionable set (disposition:pending, outcome:compound).",
  ),
  limit: z.number().min(1).max(200).optional().describe("For 'list': max rows (default 50, max 200)."),
  rowId: z.string().max(200).optional().describe("For 'show'/'agree'/'dismiss': the advisory row id."),
  reason: z.string().max(1000).optional().describe("For 'agree'/'dismiss': optional reason (capped at 1000 chars)."),
});

// §3b — the HANDLER layer: per-action discriminatedUnion.
const shapeListVariant = z.object({
  action: z.literal("list"),
  disposition: z.enum(LIST_DISPOSITIONS).optional(),
  outcome: z.enum(LIST_OUTCOMES).optional(),
  limit: z.number().min(1).max(200).optional(),
});
const shapeShowVariant = z.object({ action: z.literal("show"), rowId: z.string().max(200) });
const shapeAgreeVariant = z.object({ action: z.literal("agree"), rowId: z.string().max(200), reason: z.string().max(1000).optional() });
const shapeDismissVariant = z.object({ action: z.literal("dismiss"), rowId: z.string().max(200), reason: z.string().max(1000).optional() });

export const shapeActionUnion = z.discriminatedUnion("action", [
  shapeListVariant,
  shapeShowVariant,
  shapeAgreeVariant,
  shapeDismissVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const SHAPE_ACTION_SPECS: Record<ShapeAction, ActionParamSpec> = {
  list: { params: ["disposition", "outcome", "limit"], description: "All params optional; no filters defaults to disposition:pending + outcome:compound." },
  show: { params: ["rowId"], description: "rowId is required." },
  agree: { params: ["rowId", "reason"], description: "rowId is required; reason is optional." },
  dismiss: { params: ["rowId", "reason"], description: "rowId is required; reason is optional." },
};

export async function handleShapeList(disposition?: string, outcome?: string, limit?: number): Promise<ToolResult> {
  // Review round 4 (Codex P2): default the review queue to actionable advisories. The
  // documented list -> show -> agree/dismiss flow's normal first call passes no filters at
  // all, which requests every ledger outcome; because the backend returns only the newest 50
  // subjects, recent not_candidate/atomic/unavailable rows can push an older PENDING COMPOUND
  // row — the only outcome dispositionShapeAdvisory can actually act on — out of the page.
  // Default a fully-unfiltered call to the actionable set (disposition:pending,
  // outcome:compound); any explicitly requested filter is honored as-is, with no override.
  const noFiltersGiven = disposition === undefined && outcome === undefined;
  const effectiveDisposition = disposition ?? (noFiltersGiven ? "pending" : undefined);
  const effectiveOutcome = outcome ?? (noFiltersGiven ? "compound" : undefined);
  // WP-571 sweep fix (P1 review finding on PR #520): `chain.shapeAdvisories` was ALREADY
  // live (auto-deployed/published on every merge to main) returning a BARE ARRAY as `data` —
  // that shape must stay unchanged for already-installed clients. The LIST_SCAN_CAP
  // safety-bound signal rides `_meta.truncated` instead (convex/lib/envelopeContract.ts,
  // convex/http.ts's gateway-side unwrap for this one route), so `kernelCallEnvelope` is used
  // here (not `kernelQuery`, which strips to `.data` only) to read it back. workspaceId is
  // injected manually — kernelCallEnvelope, unlike kernelQuery, does not do this itself.
  const workspaceId = await getWorkspaceId();
  const envelope = await kernelCallEnvelope<ShapeAdvisoryItem[]>("chain.shapeAdvisories", {
    ...(effectiveDisposition ? { disposition: effectiveDisposition } : {}),
    ...(effectiveOutcome ? { outcome: effectiveOutcome } : {}),
    ...(limit ? { limit } : {}),
    workspaceId,
  });
  const items = envelope.data;
  const truncated = Boolean((envelope._meta as { truncated?: boolean } | undefined)?.truncated);
  const truncationNote = "Note: this list may be incomplete — the scan stopped at its safety bound before finding every match. Narrow disposition/outcome or raise limit and re-run.";

  if (items.length === 0) {
    return successResult(
      truncated ? `No shape advisories match.\n\n${truncationNote}` : "No shape advisories match.",
      truncated ? "No shape advisories found (result may be incomplete — safety bound reached)." : "No shape advisories found.",
      { items: [], count: 0, truncated },
    );
  }

  const lines = ["# Shape Advisories", ""];
  for (const item of items) {
    const verdict = item.outcome === "unavailable" && item.unavailableReason ? `unavailable(${item.unavailableReason})` : item.outcome;
    lines.push(`- \`${item.id}\` — ${item.subjectEntryId} · ${verdict} · disposition:${item.disposition} · ${item.source}`);
  }
  if (truncated) lines.push("", truncationNote);

  // Review round 3 (Codex P2): `next` must be an IMMEDIATELY actionable call — `action=show`
  // with no rowId fails shapeShowVariant's required param. Point at the first result's id
  // (a genuinely valid next step for an agent scanning the list) rather than an incomplete
  // recommendation; items.length > 0 is already guaranteed by the branch above.
  return successResult(
    lines.join("\n"),
    `Found ${items.length} shape advisor${items.length === 1 ? "y" : "ies"}.${truncated ? " (list may be incomplete — safety bound reached)" : ""}`,
    { items, count: items.length, truncated },
    [{ tool: "shape", description: "Show one advisory in full", parameters: { action: "show", rowId: items[0].id } }],
  );
}

/**
 * Review round 3 (Codex P2): text-content rendering of the proposed split + disposition
 * reason — MCP clients that consume only `content[0].text` (the documented backward-compatible
 * surface, envelope.ts's own header) would otherwise never see what the judge actually
 * proposed, same gap the CLI formatter (formatters/shape.ts) already closed for TTY output.
 */
function renderShapeAdvisoryText(item: ShapeAdvisoryItem): string {
  const lines = [
    `# ${item.id}`,
    `- Subject: ${item.subjectEntryId} (${item.collectionSlug})`,
    `- Outcome: ${item.outcome}`,
    `- Disposition: ${item.disposition}`,
    `- Source: ${item.source}`,
  ];
  if (item.proposedAtoms?.length) {
    lines.push("", `Proposed split (${item.proposedAtoms.length} atom(s)):`);
    item.proposedAtoms.forEach((atom, idx) => {
      lines.push(`  [${idx}] ${atom.title} — ${atom.concern}`);
      lines.push(`      "${atom.bodyExcerpt}"`);
      if (atom.suggestedCollection) lines.push(`      suggested collection: ${atom.suggestedCollection}`);
    });
  }
  if (item.proposedLinks?.length) {
    lines.push("", "Proposed links:");
    for (const link of item.proposedLinks) {
      const from = link.fromIdx === -1 ? "original" : `[${link.fromIdx}]`;
      lines.push(`  ${from} --${link.relation}--> [${link.toIdx}]`);
    }
  }
  if (item.dispositionReason) lines.push("", `- Disposition reason: ${item.dispositionReason}`);
  return lines.join("\n");
}

export async function handleShapeShow(rowId: string): Promise<ToolResult> {
  const item = await kernelQuery<ShapeAdvisoryItem | null>("chain.showShapeAdvisory", { rowId });
  if (!item) {
    return failureResult(
      `Advisory '${rowId}' not found (missing, or belongs to a different workspace).`,
      "NOT_FOUND",
      `Advisory '${rowId}' not found.`,
      "Use action=list to find valid ids.",
      [{ tool: "shape", description: "List advisories", parameters: { action: "list" } }],
    );
  }
  return successResult(
    renderShapeAdvisoryText(item),
    `Advisory ${item.id}: ${item.outcome}, disposition:${item.disposition}.`,
    item,
  );
}

export async function handleShapeDisposition(
  disposition: "agreed" | "dismissed",
  rowId: string,
  reason?: string,
): Promise<ToolResult> {
  requireWriteAccess();
  try {
    const result = await kernelMutation<DispositionReceipt>("chain.dispositionShapeAdvisory", {
      rowId,
      disposition,
      ...(reason ? { dispositionReason: reason } : {}),
    });
    return successResult(
      `Advisory ${result.rowId} dispositioned: ${result.disposition}.`,
      `Advisory dispositioned: ${result.disposition}.`,
      result,
    );
  } catch (err: unknown) {
    // Review round 2 (Codex P2): classify via the SAME classifyError thinWrapper's own
    // catch boundary uses, instead of collapsing every backend failure into a generic
    // "DISPOSITION_ERROR" — preserves the real NOT_FOUND/VALIDATION_ERROR code
    // dispositionShapeAdvisory's structured ConvexError throws carry, so an agent can tell
    // "already dispositioned" apart from "row doesn't exist" instead of a bare 500-shaped blob.
    const classified = classifyError(err);
    // Review round 4 (Codex P2): classifyError's own NOT_FOUND branch always sets a generic
    // entries-oriented recovery + availableActions ("Use entries action=search..."), so the
    // `??` fallbacks below never select — an agent following that guidance can't discover a
    // shape-advisory row id via `entries action=search` at all. Override NOT_FOUND here with
    // `shape action=list`, the recovery that can actually locate a valid advisory.
    const isNotFound = classified.code === "NOT_FOUND";
    return failureResult(
      classified.message,
      classified.code,
      classified.message,
      isNotFound
        ? "Use action=list to find valid ids."
        : (classified.recovery ?? "Use action=show to check the advisory's current outcome/disposition."),
      isNotFound
        ? [{ tool: "shape", description: "List advisories", parameters: { action: "list" } }]
        : (classified.availableActions ?? [{ tool: "shape", description: "Show the advisory", parameters: { action: "show", rowId } }]),
      classified.diagnostics as Record<string, unknown> | undefined,
    );
  }
}

export function registerShapeTools(server: McpServer): void {
  // §3b handler map — one entry per action, dispatched via dispatchDiscriminated.
  const shapeHandlers: Record<ShapeAction, (data: any) => Promise<ToolResult>> = {
    list: (data) => handleShapeList(data.disposition, data.outcome, data.limit),
    show: (data) => handleShapeShow(data.rowId),
    agree: (data) => handleShapeDisposition("agreed", data.rowId, data.reason),
    dismiss: (data) => handleShapeDisposition("dismissed", data.rowId, data.reason),
  };

  server.registerTool(
    "shape",
    {
      title: "Shape",
      description:
        "Review and disposition shape-advisory (single-concern split) suggestions for this workspace. Four actions:\n\n" +
        "- **list**: List shape advisories, latest per subject (filter by disposition/outcome).\n" +
        "- **show**: Show one shape advisory in full by id.\n" +
        "- **agree**: Agree with a compound advisory's split verdict.\n" +
        "- **dismiss**: Dismiss a compound advisory's split verdict.",
      inputSchema: shapeSchema,
      // Mixed read/write noun (§3 R3, same as workspace's proposals-respond): agree/dismiss write;
      // list/show read.
      annotations: { readOnlyHint: false, idempotentHint: false, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(shapeSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "shape", action }, () =>
        dispatchDiscriminated("shape", shapeActionUnion, parsed.data, SHAPE_ACTION_SPECS, shapeHandlers),
      );
    }),
  );
}
